import { useState } from 'react'
import ModalPortal from '../UI/ModalPortal'
import { X, ArrowRightLeft } from 'lucide-react'
import { TextField, Autocomplete } from '@anyemp/ui-kit'
import { talents } from '../../services/apiService'
import { formatApiError } from '../../utils/formatApiError'
import {
  TALENT_ONLY_TYPES,
  getTalentTypeLabel,
  getTalentTypeHexColor
} from '../../utils/talentTypeUtils'

/** Причина уезжает в event_data.talent_type_changed.reason; ограничиваем как у статусов */
const TYPE_REASON_MAX_LENGTH = 500

/** Причина по умолчанию: бэкенд без неё отдаёт 400, а руками её пишут не всегда */
const defaultReason = (fromType, toType) => (fromType && toType
  ? `Moved from ${getTalentTypeLabel(fromType)} to ${getTalentTypeLabel(toType)}`
  : '')

/**
 * Смена типа таланта — из карточки и из списка.
 *
 * Ходит в PATCH /talents/:id/type. Общий PUT сюда не годится: он статус на
 * новый тип не переносит и такую пару отклоняет.
 *
 * initialType / initialReason — для вызова из списка, где тип уже выбран
 * кликом по чипу, а причина берётся из конфига страницы. Поле причины
 * остаётся редактируемым: обязательность на бэкенде мы закрываем подстановкой,
 * а не требованием к человеку.
 */
const TalentTypeChangeModal = ({
  isOpen,
  onClose,
  talent,
  initialType = null,
  initialReason = '',
  onSuccess
}) => {
  const [newType, setNewType] = useState(initialType)
  const [reason, setReason] = useState(
    initialReason || defaultReason(talent?.talent_type, initialType)
  )
  // Пока причину не трогали руками, она следует за выбранным типом
  const [reasonTouched, setReasonTouched] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  if (!isOpen || !talent) return null

  const typeOptions = TALENT_ONLY_TYPES
    .filter((type) => type !== talent.talent_type)
    .map((type) => ({ value: type, label: getTalentTypeLabel(type) }))

  const canSubmit = !!newType && !loading

  const handleTypeSelect = (value) => {
    setNewType(value)
    if (!reasonTouched) setReason(defaultReason(talent.talent_type, value))
  }

  const handleSubmit = async () => {
    if (!canSubmit) return
    try {
      setLoading(true)
      setError(null)
      const finalReason = reason.trim() || defaultReason(talent.talent_type, newType)
      await talents.changeTalentType(talent.id, newType, finalReason)
      onSuccess?.(newType)
    } catch (err) {
      setError(formatApiError(err, 'Failed to change talent type'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <ModalPortal onClose={loading ? undefined : onClose} ariaLabel="Change talent type">
      <div className="mx-4 flex max-h-[calc(100dvh-1.5rem)] w-full max-w-md min-w-0 flex-col overflow-hidden rounded-lg bg-light-bg-primary shadow-xl dark:bg-dark-bg-secondary sm:max-h-[calc(100dvh-4rem)]">
        {/* Header */}
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-light-border-primary p-4 dark:border-dark-border-primary sm:p-6">
          <div className="flex min-w-0 items-center gap-3">
            <ArrowRightLeft className="h-6 w-6 text-primary-600 dark:text-primary-400 shrink-0" />
            <h3 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
              Change talent type
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-light-text-tertiary hover:text-light-text-primary dark:text-dark-text-tertiary dark:hover:text-dark-text-primary disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">
          <div className="flex items-center gap-2 flex-wrap text-sm">
            <span className="text-light-text-secondary dark:text-dark-text-secondary">Current type:</span>
            <span
              className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-md text-white"
              style={{ backgroundColor: getTalentTypeHexColor(talent.talent_type) }}
            >
              {getTalentTypeLabel(talent.talent_type)}
            </span>
            {talent.currentStatus?.name && (
              <span className="text-light-text-tertiary dark:text-dark-text-tertiary text-xs">
                · status {talent.currentStatus.name}
              </span>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-1">
              New type <span className="text-red-500">*</span>
            </label>
            <Autocomplete
              fullWidth
              options={typeOptions}
              value={newType}
              onChange={(value) => handleTypeSelect(value || '')}
              placeholder="Select new type"
            />
          </div>

          {/* Тип таланта задаёт стадия, поэтому перевод меняет стадию и статус —
              предупреждаем до подтверждения, а не постфактум в истории */}
          <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
            The talent type is defined by the stage, so the talent will be moved to the first
            stage of the new type and get its default status. The talent ID will be regenerated.
            All changes are recorded in the profile history.
          </p>

          <TextField
            label="Reason"
            multiline
            rows={3}
            fullWidth
            value={reason}
            onChange={(e) => {
              setReasonTouched(true)
              setReason(e.target.value)
            }}
            maxLength={TYPE_REASON_MAX_LENGTH}
            placeholder="Goes into the profile history"
          />

          {error && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-md px-3 py-2">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-light-border-primary p-4 pb-[max(1rem,env(safe-area-inset-bottom))] dark:border-dark-border-primary sm:flex-row sm:justify-end sm:p-6">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium rounded-md border border-light-border-primary dark:border-dark-border-primary text-light-text-primary dark:text-dark-text-primary bg-light-bg-primary dark:bg-dark-bg-tertiary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-primary transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {loading && (
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            )}
            Change type
          </button>
        </div>
      </div>
    </ModalPortal>
  )
}

export default TalentTypeChangeModal
